// ============================================
// STATUTS DES DOSSIERS — sens, parcours, transitions
// ============================================
// Demande de Nathan (19/10/2026) : « les statuts doivent être plus
// compréhensibles ». Chaque statut a une phrase qui dit ce qu'il signifie et
// ce qu'on attend, et une place dans le parcours d'un dossier :
//   Ouvert → En cours → Attente client → Résolu → Fermé.
// Escaladé et Réouvert sont des états particuliers : ils prennent la place
// d'une étape du parcours (En cours pour Escaladé, Ouvert pour Réouvert).
//
// Les transitions proposées sont celles de la barre d'actions de la fiche
// (Démarrer, Résoudre, Attente client, Reprendre, Archiver, Réouvrir,
// Désarchiver, Escalader). Archiver (19/10/2026) = passer en « Fermé » :
// possible depuis tout statut, le dossier rejoint l'onglet Archives. Le
// champ « Statut » de l'onglet Détails reste le moyen de tout changer.
// Helpers purs, sans React.

import { TicketStatus } from '../types';

/** Ce que veut dire le statut, et ce qu'on attend (affiché sous l'étape courante et dans la légende). */
export const SENS_STATUT: Record<TicketStatus, { resume: string; attente: string }> = {
  [TicketStatus.OPEN]: {
    resume: 'Nouveau dossier : personne ne s’en occupe encore.',
    attente: 'À prendre en charge par un conseiller.',
  },
  [TicketStatus.IN_PROGRESS]: {
    resume: 'Un conseiller travaille sur le dossier.',
    attente: 'La balle est chez nous : diagnostic, réponse ou intervention à faire.',
  },
  [TicketStatus.WAITING_CUSTOMER]: {
    resume: 'La balle est chez le client : il doit répondre.',
    attente: 'Sa réponse remet le dossier « En cours ». Sans réponse, des relances automatiques partent si elles sont activées dans les paramètres.',
  },
  [TicketStatus.REOPENED]: {
    resume: 'Le dossier a été rouvert après sa résolution ou sa clôture.',
    attente: 'À reprendre : relire le dernier message du client.',
  },
  [TicketStatus.ESCALATED]: {
    resume: 'Le dossier est remonté à un responsable.',
    attente: 'Attention requise : un responsable doit trancher.',
  },
  [TicketStatus.RESOLVED]: {
    resume: 'Une solution a été apportée.',
    attente: 'Plus rien à faire, sauf si le client revient : le dossier est alors rouvert.',
  },
  [TicketStatus.CLOSED]: {
    resume: 'Dossier terminé et archivé.',
    attente: 'Rien à faire : il est dans les Archives. « Désarchiver » le remet en traitement ; un e-mail du client le rouvre aussi.',
  },
};

/** Phrase complète (infobulle d'un badge, légende). */
export const explicationStatut = (s: TicketStatus | string): string => {
  const sens = SENS_STATUT[s as TicketStatus];
  return sens ? `${sens.resume} ${sens.attente}` : '';
};

/** Les cinq étapes du parcours ordinaire d'un dossier. */
export const PARCOURS_PRINCIPAL: readonly TicketStatus[] = [
  TicketStatus.OPEN,
  TicketStatus.IN_PROGRESS,
  TicketStatus.WAITING_CUSTOMER,
  TicketStatus.RESOLVED,
  TicketStatus.CLOSED,
];

/** États particuliers : l'étape du parcours dont ils prennent la place. */
export const PLACE_ETAT_PARTICULIER: Partial<Record<TicketStatus, TicketStatus>> = {
  [TicketStatus.ESCALATED]: TicketStatus.IN_PROGRESS,
  [TicketStatus.REOPENED]: TicketStatus.OPEN,
};

export const estEtatParticulier = (s: TicketStatus | string): boolean =>
  (s as TicketStatus) in PLACE_ETAT_PARTICULIER;

export type EtatEtape = 'passee' | 'courante' | 'future';

export interface EtapeParcours {
  /** Étape du parcours ordinaire. */
  etape: TicketStatus;
  /** Statut affiché sur cette étape : l'étape elle-même, ou l'état particulier qui la remplace. */
  affiche: TicketStatus;
  etat: EtatEtape;
}

/**
 * Étapes du parcours pour un statut : celles d'avant sont passées (cochées),
 * celle du statut est courante, les suivantes sont à venir. Un état
 * particulier (Escaladé, Réouvert) s'affiche à la place de son étape.
 * Statut inconnu : tout est à venir.
 */
export function etapesParcours(status: TicketStatus | string): EtapeParcours[] {
  const s = status as TicketStatus;
  const place = PLACE_ETAT_PARTICULIER[s] ?? s;
  const rang = PARCOURS_PRINCIPAL.indexOf(place);
  return PARCOURS_PRINCIPAL.map((etape, i) => ({
    etape,
    affiche: i === rang ? s : etape,
    etat: rang < 0 ? 'future' : i < rang ? 'passee' : i === rang ? 'courante' : 'future',
  }));
}

/** Rang (1 à 5) de l'étape courante dans le parcours, 0 si statut inconnu. */
export const rangParcours = (status: TicketStatus | string): number =>
  etapesParcours(status).findIndex(e => e.etat === 'courante') + 1;

/** Archiver = passer en « Fermé » ; désarchiver = repasser en « Réouvert ». */
export const LIBELLE_ARCHIVER = 'Archiver';
export const LIBELLE_DESARCHIVER = 'Désarchiver';

export interface ActionStatut {
  vers: TicketStatus;
  /** Libellé du bouton (« Démarrer », « Résoudre »…). */
  libelle: string;
  /** Action principale (bouton plein) ou secondaire. */
  principale: boolean;
  /** Raccourci clavier affiché à côté du libellé. */
  raccourci?: string;
}

/**
 * Changements de statut proposés par la barre d'actions de la fiche, dans
 * l'ordre d'affichage. C'est la seule source : la barre, le parcours et son
 * menu sur téléphone en dépendent.
 */
export function actionsStatut(status: TicketStatus | string): ActionStatut[] {
  const s = status as TicketStatus;
  const actions: ActionStatut[] = [];
  switch (s) {
    case TicketStatus.OPEN:
      actions.push({ vers: TicketStatus.IN_PROGRESS, libelle: 'Démarrer', principale: true });
      break;
    case TicketStatus.IN_PROGRESS:
      actions.push({ vers: TicketStatus.RESOLVED, libelle: 'Résoudre', principale: true, raccourci: 'R' });
      actions.push({ vers: TicketStatus.WAITING_CUSTOMER, libelle: 'Attente client', principale: false });
      break;
    case TicketStatus.WAITING_CUSTOMER:
      actions.push({ vers: TicketStatus.IN_PROGRESS, libelle: 'Reprendre', principale: true });
      break;
    case TicketStatus.RESOLVED:
      actions.push({ vers: TicketStatus.CLOSED, libelle: LIBELLE_ARCHIVER, principale: false });
      actions.push({ vers: TicketStatus.REOPENED, libelle: 'Réouvrir', principale: false });
      break;
    case TicketStatus.CLOSED:
      // Un dossier fermé est archivé : on le désarchive (il repasse « Réouvert »).
      actions.push({ vers: TicketStatus.REOPENED, libelle: LIBELLE_DESARCHIVER, principale: false });
      break;
    case TicketStatus.REOPENED:
      actions.push({ vers: TicketStatus.IN_PROGRESS, libelle: 'Reprendre', principale: true });
      break;
    case TicketStatus.ESCALATED:
      // Sortie d'une escalade (constructeur ou responsable a répondu) :
      // sans elle, seul le champ Statut de l'onglet Détails le permettait.
      actions.push({ vers: TicketStatus.IN_PROGRESS, libelle: 'Reprendre', principale: true });
      actions.push({ vers: TicketStatus.RESOLVED, libelle: 'Résoudre', principale: false, raccourci: 'R' });
      break;
    default:
      break;
  }
  if (s !== TicketStatus.ESCALATED && s !== TicketStatus.CLOSED) {
    actions.push({ vers: TicketStatus.ESCALATED, libelle: 'Escalader', principale: false, raccourci: 'E' });
  }
  // Archiver (passer en « Fermé ») : depuis tout statut qui ne le propose pas déjà.
  if (s !== TicketStatus.CLOSED && s in SENS_STATUT && !actions.some(a => a.vers === TicketStatus.CLOSED)) {
    actions.push({ vers: TicketStatus.CLOSED, libelle: LIBELLE_ARCHIVER, principale: false });
  }
  return actions;
}

/** Statuts vers lesquels on peut passer depuis `status` (mêmes règles que la barre). */
export const transitionsDepuis = (status: TicketStatus | string): TicketStatus[] =>
  actionsStatut(status).map(a => a.vers);
